import {
  Badge,
  Button,
  Card,
  Group,
  Image,
  Paper,
  Skeleton,
  Stack,
  Text,
  Title,
  Box,
  AspectRatio,
} from "@mantine/core";
import {
  IconCalendar,
  IconCloudRain,
  IconPlus,
  IconUsers,
  IconPhoto,
} from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useDailyLogs, type DailyLog } from "@/hooks/useDailyLogs";
import { SyncStatusBadge } from "@/components/OfflineIndicator";

export const Route = createFileRoute(
  "/projects/$projectId/daily-logs/" as any
)({
  component: DailyLogsPage,
});

function DailyLogsPage() {
  const params = Route.useParams();
  const projectId = "projectId" in params ? params.projectId : "";
  const navigate = useNavigate();
  const { data, isLoading } = useDailyLogs(projectId);

  const handleAddLog = () => {
    navigate({
      to: "/projects/$projectId/daily-logs/new",
      params: { projectId },
    });
  };

  const handleLogClick = (logId: string) => {
    navigate({
      to: "/projects/$projectId/daily-logs/$logId",
      params: { projectId, logId },
    });
  };

  if (isLoading) {
    return (
      <Paper p="xl" withBorder>
        <Stack gap="lg">
          <Skeleton height={40} width={200} />
          <Stack gap="md">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} height={150} radius="md" />
            ))}
          </Stack>
        </Stack>
      </Paper>
    );
  }

  const dailyLogs = data?.dailyLogs || [];

  if (dailyLogs.length === 0) {
    return (
      <Paper p="xl" withBorder>
        <Stack gap="lg">
          <Group justify="space-between" align="center">
            <div>
              <Title order={2}>Progress</Title>
              <Text size="sm" c="dimmed">Track what's happening on your project</Text>
            </div>
            <Button
              leftSection={<IconPlus size={20} />}
              onClick={handleAddLog}
              size="md"
            >
              Add Update
            </Button>
          </Group>

          <Paper p="xl" withBorder style={{ borderStyle: "dashed" }}>
            <Stack gap="md" align="center">
              <IconCalendar size={48} stroke={1.5} color="var(--mantine-color-gray-5)" />
              <Text c="dimmed" ta="center">
                No updates yet
              </Text>
              <Text size="sm" c="dimmed" ta="center">
                Document what's happening on site - work completed, issues found, or anything notable
              </Text>
              <Button
                leftSection={<IconPlus size={20} />}
                onClick={handleAddLog}
                size="md"
              >
                Add Your First Update
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </Paper>
    );
  }

  // Sort logs by date descending (newest first)
  const sortedLogs = [...dailyLogs].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  return (
    <Paper p="xl" withBorder>
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <Title order={2}>Progress</Title>
          <Button
            leftSection={<IconPlus size={20} />}
            onClick={handleAddLog}
            size="md"
          >
            Add Update
          </Button>
        </Group>

        <Stack gap="md">
          {sortedLogs.map((log) => (
            <DailyLogCard
              key={log.id}
              log={log}
              onClick={() => handleLogClick(log.id)}
            />
          ))}
        </Stack>
      </Stack>
    </Paper>
  );
}

interface DailyLogCardProps {
  log: DailyLog;
  onClick: () => void;
}

function DailyLogCard({ log, onClick }: DailyLogCardProps) {
  const formatDate = (dateString: string) => {
    // Parse date as local timezone to avoid UTC conversion issues
    // dateString format is "YYYY-MM-DD"
    const parts = dateString.split("-").map(Number);
    const year = parts[0] ?? new Date().getFullYear();
    const month = parts[1] ?? 1;
    const day = parts[2] ?? 1;
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getWeatherIcon = (conditions: string) => {
    const lower = conditions.toLowerCase();
    if (lower.includes("rain") || lower.includes("storm")) {
      return <IconCloudRain size={20} />;
    }
    // Default weather icon - could expand this
    return <IconCloudRain size={20} />;
  };

  // Truncate work performed for preview
  const workPreview =
    log.workPerformed.length > 120
      ? `${log.workPerformed.slice(0, 120)}...`
      : log.workPerformed;

  // Get first photo if available
  const firstPhoto = log.photos?.[0];
  const photoCount = log.photos?.length || 0;

  return (
    <Card
      shadow="sm"
      padding="lg"
      radius="md"
      withBorder
      style={{ cursor: "pointer" }}
      onClick={onClick}
    >
      <Stack gap="sm">
        {/* Date and weather header */}
        <Group justify="space-between" align="flex-start">
          <div>
            <Group gap="xs">
              <IconCalendar size={18} stroke={1.5} />
              <Text fw={600} size="lg">
                {formatDate(log.date)}
              </Text>
              {log.syncStatus && (
                <SyncStatusBadge status={log.syncStatus} error={log.syncError} />
              )}
            </Group>
          </div>

          <Group gap="md">
            <Badge variant="light" size="lg" leftSection={getWeatherIcon(log.weather.conditions)}>
              {log.weather.temp}°F - {log.weather.conditions}
            </Badge>
            <Badge variant="light" size="lg" leftSection={<IconUsers size={16} />}>
              {log.crewCount} crew
            </Badge>
          </Group>
        </Group>

        {/* Main content area with optional thumbnail */}
        <Group align="flex-start" gap="md" wrap="nowrap">
          {/* Photo thumbnail */}
          {firstPhoto && (
            <Box style={{ flexShrink: 0 }}>
              <AspectRatio ratio={1} w={60}>
                <Image
                  src={`/api/projects/${log.projectId}/photos/${firstPhoto.id}/file`}
                  alt="Daily log photo"
                  radius="sm"
                  fit="cover"
                  fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60'%3E%3Crect width='60' height='60' fill='%23f1f3f5'/%3E%3C/svg%3E"
                />
              </AspectRatio>
            </Box>
          )}

          {/* Work performed preview */}
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" c="dimmed" lineClamp={2}>
              {workPreview}
            </Text>
          </Box>
        </Group>

        {/* Footer with issues and photo count */}
        <Group gap="xs">
          {log.issues && (
            <Badge color="orange" variant="light">
              Has Issues
            </Badge>
          )}
          {photoCount > 0 && (
            <Badge variant="light" leftSection={<IconPhoto size={14} />}>
              {photoCount} {photoCount === 1 ? "photo" : "photos"}
            </Badge>
          )}
        </Group>
      </Stack>
    </Card>
  );
}
